import { useState, type FormEvent } from 'react';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { Logo } from './Logo';
import { Input } from './Field';
import { Button } from './Button';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginBranding {
  /** Left brand panel copy. */
  panelEyebrow: string;
  panelHeading: string;
  panelBody: string;
  panelFootnote: string;
  /** Right form copy. */
  formEyebrow: string;
  formHeading: string;
  formSubtext: string;
  emailPlaceholder: string;
  formFootnote: string;
}

export interface LoginScreenProps {
  branding: LoginBranding;
  /** Background image for the dark brand panel (imported asset URL). */
  image?: string;
  /** Resolve a thrown error into a user-facing message. */
  resolveError?: (err: unknown) => string;
  onSubmit: (credentials: LoginCredentials) => Promise<void>;
}

/**
 * Shared split-screen login used by every TechSoFit portal (spec §10). A dark
 * brand panel (optionally over a gym photo) beside the credential form. The
 * caller supplies copy, an image, the submit handler and an error mapper; this
 * component owns the loading/error UI so apps don't duplicate it.
 */
export function LoginScreen({ branding: b, image, resolveError, onSubmit }: LoginScreenProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const form = new FormData(e.currentTarget);
    try {
      await onSubmit({
        email: String(form.get('email') ?? ''),
        password: String(form.get('password') ?? ''),
      });
    } catch (err) {
      setError(resolveError ? resolveError(err) : 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="grid min-h-dvh bg-canvas p-3 md:p-4 lg:grid-cols-[1.05fr_1fr] lg:gap-4">
      {/* Brand panel */}
      <aside className="relative hidden overflow-hidden rounded-card bg-dark p-10 text-on-dark lg:flex lg:flex-col">
        {image && (
          <>
            <img src={image} alt="" aria-hidden className="absolute inset-0 size-full object-cover" />
            {/* Dark gradient so copy stays legible over the photo. */}
            <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/90 to-dark/55" />
          </>
        )}

        <div className="relative">
          <Logo onDark />
        </div>

        <div className="relative mt-auto">
          <p className="text-eyebrow uppercase text-accent">{b.panelEyebrow}</p>
          <h2 className="mt-4 max-w-md font-display text-[2.6rem] font-semibold leading-[1.05] tracking-tight">
            {b.panelHeading}
          </h2>
          <p className="mt-4 max-w-sm text-on-dark-muted">{b.panelBody}</p>

          <div className="mt-8 flex items-center gap-2 text-sm text-on-dark-muted">
            <ShieldCheck className="size-4 text-accent" aria-hidden />
            {b.panelFootnote}
          </div>
        </div>

        {/* Signature: an oversized accent pulse line bleeding off the corner */}
        <svg
          className="pointer-events-none absolute -right-10 -top-10 size-72 opacity-20"
          viewBox="0 0 200 200"
          fill="none"
          aria-hidden
        >
          <path d="M0 120h40l24-70 36 130 26-84 18 26H200" stroke="#E2F26B" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </aside>

      {/* Form */}
      <main className="flex items-center justify-center rounded-card bg-card p-6 md:p-10">
        <form onSubmit={handleSubmit} className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 lg:hidden">
            <Logo />
          </div>

          <p className="text-eyebrow uppercase text-muted">{b.formEyebrow}</p>
          <h1 className="mt-2 font-display text-display-md text-ink">{b.formHeading}</h1>
          <p className="mt-2 text-sm text-ink-soft">{b.formSubtext}</p>

          <div className="mt-8 flex flex-col gap-4">
            <Input
              label="Email"
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              placeholder={b.emailPlaceholder}
              required
            />
            <Input
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              placeholder="••••••••"
              required
              aside={
                <a href="#" className="text-xs font-medium text-ink-soft underline-offset-2 hover:underline">
                  Forgot password?
                </a>
              }
            />

            {error && (
              <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2.5 text-sm font-medium text-bad-ink">
                {error}
              </p>
            )}

            <Button type="submit" size="lg" block loading={loading} className="mt-2">
              Sign in
              {!loading && <ArrowRight className="size-4" aria-hidden />}
            </Button>
          </div>

          <p className="mt-8 text-center text-xs text-muted">{b.formFootnote}</p>
        </form>
      </main>
    </div>
  );
}
